import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../config";
import Topbar from "../../components/topbar/Topbar";
import Leftbar from "../../components/LeftBar/lefbar";
import "../me/mepage.css";
import "./analyticsPage.css";

// Analytics tab: how the creator's TikTok videos perform, and which came from
// Clipbait. Admin-only until TikTok approves the app (the API returns 403).

const fmt = (n) => (n == null ? "–" : Intl.NumberFormat("en", { notation: n >= 10000 ? "compact" : "standard" }).format(n));
const engagement = (v) => (v.views ? (v.likes + v.comments + v.shares) / v.views : 0);

function tipFor(data) {
  const { avgViews, videos } = data;
  if (!videos.length) return "Post a few clips to TikTok and your numbers will show up here.";
  if (avgViews.clipbait != null && avgViews.other != null) {
    const diff = avgViews.clipbait - avgViews.other;
    if (diff > 0) return `Clips from Clipbait average ${fmt(diff)} more views than your other videos. Post them more often.`;
    if (diff < 0) return `Your other videos average ${fmt(-diff)} more views than Clipbait clips. Compare their first 2 seconds: the hook decides most of the views.`;
  }
  const best = [...videos].sort((a, b) => engagement(b) - engagement(a))[0];
  if (best?.views) {
    return `"${(best.title || "Your top video").slice(0, 60)}" gets the most likes, comments and shares per view (${(engagement(best) * 100).toFixed(1)}%). Make more like it.`;
  }
  return "Views take a few hours to come in after posting. Check back later.";
}

const STATUS_LABEL = { posted: "Posted", processing: "Processing", failed: "Failed", pending: "Pending" };

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [notConnected, setNotConnected] = useState(false);

  useEffect(() => {
    axiosInstance
      .get("/tiktok/analytics")
      .then((res) => setData(res.data))
      .catch((err) => {
        if (err.response?.status === 403) return setError("Analytics isn't available yet.");
        if (err.response?.status === 400) return setNotConnected(true);
        setError(err.response?.data?.error || "Couldn't load your TikTok stats.");
      });
  }, []);

  return (
    <div className="me-page">
      <Topbar />
      <div className="me-leftbar-wrapper">
        <Leftbar />
      </div>

      <div className="me-content an-content">
        <h2 className="an-title">Analytics</h2>

        {notConnected ? (
          <div className="an-empty">
            <p>Connect TikTok to see how your clips perform.</p>
            <button onClick={() => navigate("/me")}>Connect TikTok</button>
          </div>
        ) : error ? (
          <p className="an-error">{error}</p>
        ) : !data ? (
          <p className="an-muted">Loading your TikTok stats…</p>
        ) : (
          <>
            <div className="an-profile">
              {data.profile.avatarUrl && <img src={data.profile.avatarUrl} alt="" />}
              <div>
                <strong>{data.profile.displayName}</strong>
                {data.profile.username && <span>@{data.profile.username} · TikTok</span>}
              </div>
            </div>

            <div className="an-tiles">
              <div className="an-tile"><span>Followers</span><strong>{fmt(data.profile.followers)}</strong></div>
              <div className="an-tile"><span>Total likes</span><strong>{fmt(data.profile.likes)}</strong></div>
              <div className="an-tile"><span>Views, recent videos</span><strong>{fmt(data.totals.views)}</strong></div>
              <div className="an-tile"><span>Avg views per video</span><strong>{fmt(data.avgViews.all)}</strong></div>
            </div>

            <div className="an-tip">
              <span>Tip</span>
              <p>{tipFor(data)}</p>
            </div>

            {(data.avgViews.clipbait != null) && (
              <div className="an-compare">
                <div><span>Clipbait clips</span><strong>{fmt(data.avgViews.clipbait)}</strong><small>avg views</small></div>
                <div><span>Your other videos</span><strong>{fmt(data.avgViews.other)}</strong><small>avg views</small></div>
              </div>
            )}

            <h3 className="me-section-title">Your recent TikTok videos</h3>
            {data.videos.length === 0 ? (
              <p className="an-muted">No public videos yet. Videos posted as "Only me" don't appear here.</p>
            ) : (
              <ul className="an-videos">
                {data.videos.map((v) => (
                  <li key={v.id}>
                    <a href={v.url} target="_blank" rel="noopener noreferrer" className="an-cover">
                      {v.coverUrl && <img src={v.coverUrl} alt="" loading="lazy" />}
                    </a>
                    <div className="an-video-body">
                      <p className="an-video-title">
                        {v.fromClipbait && <span className="an-badge">Clipbait</span>}
                        {v.clipTitle || v.title || "Untitled"}
                      </p>
                      <div className="an-stats">
                        <span><strong>{fmt(v.views)}</strong> views</span>
                        <span><strong>{fmt(v.likes)}</strong> likes</span>
                        <span><strong>{fmt(v.comments)}</strong> comments</span>
                        <span><strong>{fmt(v.shares)}</strong> shares</span>
                      </div>
                      {v.createdAt && <small>{new Date(v.createdAt).toLocaleDateString()}</small>}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {data.clipbaitPosts.length > 0 && (
              <>
                <h3 className="me-section-title">Sent from Clipbait</h3>
                <ul className="an-sent">
                  {data.clipbaitPosts.map((p) => (
                    <li key={p.id}>
                      <div>
                        <p>{p.clipTitle || "Clip"}</p>
                        <small>
                          {p.mode === "draft" ? "Sent to drafts" : STATUS_LABEL[p.status] || p.status}
                          {" · "}
                          {new Date(p.createdAt).toLocaleString()}
                          {p.error ? ` · ${p.error}` : ""}
                        </small>
                      </div>
                      {p.postUrl && p.mode !== "draft" && (
                        <a href={p.postUrl} target="_blank" rel="noopener noreferrer">Open</a>
                      )}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}
